<template>
  <router-link
    class="scheduled-card"
    :to="projectPath(item.project)"
    :aria-label="cardAriaLabel"
  >
    <span class="scheduled-card__media">
      <img :src="item.project.project_thumb" :alt="item.project.name" loading="lazy" @error="onImageError" />
      <span
        class="scheduled-card__age-rating"
        :class="ageRatingClass"
        :style="ageRatingStyle"
        :aria-label="`Classificação indicativa ${ageRatingLabel}`"
      >
        {{ ageRatingLabel }}
      </span>
    </span>
    <span class="scheduled-card__title">{{ item.project.name }}</span>
    <span class="scheduled-card__date" :aria-label="`Lançamento em ${formatScheduledAt(item.scheduled_at)}`">
      {{ scheduledDay }}
    </span>
  </router-link>
</template>

<script>
import moment from "moment";
import { projectPath } from "@/utils/projectRoutes";
import { HOME_PROJECT_FALLBACK, setImageFallback } from "@/utils/homeImageFallbacks";

export default {
  name: "ScheduledCard",
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ageRating() {
      return this.item?.project?.age_rating || null;
    },
    ageRatingLabel() {
      if (!this.ageRating) return "Livre";
      return this.ageRating.rating || `A${this.ageRating.age}`;
    },
    ageRatingClass() {
      if (!this.ageRating || !this.ageRating.age) return "scheduled-card__age-rating--free";
      return `scheduled-card__age-rating--${this.ageRating.age}`;
    },
    ageRatingStyle() {
      if (!this.ageRating || !this.ageRating.age) return {};
      return {
        backgroundColor: this.ratingColor(this.ageRating.age),
        color: Number(this.ageRating.age) === 12 ? "#000" : "#fff",
      };
    },
    scheduledDay() {
      const parsed = moment(this.item.scheduled_at, "DD/MM/YYYY HH:mm:ss", true);
      if (parsed.isValid()) return parsed.format("DD");
      const iso = moment(this.item.scheduled_at);
      if (iso.isValid()) return iso.format("DD");
      return "";
    },
    cardAriaLabel() {
      const type = this.item.type === "launch" ? "lançamento" : "novo capítulo";
      return `Abrir obra ${this.item.project.name} — ${type} agendado para ${this.formatScheduledAt(this.item.scheduled_at)}`;
    },
  },
  methods: {
    formatScheduledAt(value) {
      if (!value) return "";
      const parsed = moment(value, "DD/MM/YYYY HH:mm:ss", true);
      if (parsed.isValid()) {
        return parsed.format("DD/MM/YYYY [às] HH:mm");
      }
      const iso = moment(value);
      if (iso.isValid()) {
        return iso.format("DD/MM/YYYY [às] HH:mm");
      }
      return value;
    },
    ratingColor(age) {
      if (age <= 10) return "#0095DA";
      if (age <= 12) return "#FBC115";
      if (age <= 14) return "#F58220";
      return "#EC1D25";
    },
    projectPath,
    onImageError(event) {
      setImageFallback(event, HOME_PROJECT_FALLBACK);
    },
  },
};
</script>

<style scoped>
.scheduled-card {
  flex: 0 0 calc(20% - 16px);
  width: calc(20% - 16px);
  display: flex;
  flex-direction: column;
  color: inherit;
  text-decoration: none;
}

@media (min-width: 500px) and (max-width: 989px) {
  .scheduled-card {
    flex: 0 0 calc(33.33% - 16px);
    width: calc(33.33% - 16px);
  }
}

@media (max-width: 499px) {
  .scheduled-card {
    flex: 0 0 calc(50% - 16px);
    width: calc(50% - 16px);
  }
}

.scheduled-card__media {
  position: relative;
  display: block;
  aspect-ratio: 2 / 3;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  background: #1b1b1b;
  box-shadow: 0 12px 30px -12px rgba(0, 0, 0, 0.85);
  transition: transform 0.3s ease;
}

.scheduled-card:hover .scheduled-card__media {
  transform: translateY(-1px);
}

.scheduled-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.scheduled-card__age-rating {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;
  min-width: 26px;
  height: 22px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #ffffff;
  border-radius: 4px;
  color: #ffffff;
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.scheduled-card__age-rating--free {
  background-color: #2e7d32;
}

.scheduled-card__title {
  margin-top: 8px;
  background: transparent;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scheduled-card__date {
  margin-top: 6px;
  display: block;
  padding: 5px 0;
  border-radius: 999px;
  background: linear-gradient(180deg, #7a23bd 0%, #4e008d 100%);
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  text-align: center;
  line-height: 1.2;
}
</style>